import React, { Component } from 'react';
import { CaseSettings } from '../../domain/case/CaseDomains';
import { query } from '@trlogic/client-js';
import { TransactionData } from '../../domain/stream/transaction/TransactionDomains';
import TransactionPageUtil from './TransactionPageUtil';
import CaseService from '../../service/CaseService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import CaseTransactionsActivityComponent from '../../components/investigation/case/CaseTransactionsActivityComponent';
import { RootState } from '../../redux/store';
import { UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import { CaseState } from '../../redux/reducers/CaseReducer';
import { connect } from 'react-redux';
import { changePageFilter, clearPage } from '../../redux/actions/PageActions';
import { changeCaseColumns, changeListComponentLoading, changeTransactionCountLoading } from '../../redux/actions/CaseActions';
import { setAttribute } from '../../redux/actions/UserActions';
import { ConnectedProps } from 'react-redux/es/exports';
import _ from 'lodash';
import axios, { Canceler } from 'axios';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import IntervalManager from '../../util/IntervalManager';
import DocumentLanguage from '../../util/DocumentLanguage';
import Filter = query.Filter;
import Order = query.Order;

class TransactionListComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly transactionService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private readonly intervalManager: IntervalManager = InstanceContainer.get(Injectables.INTERVAL_MANAGER);

  private transactionDataCanceler: Canceler | undefined;

  public render() {
    const { loading, transactions, totalRecord, rows, currentPage, sort } = this.state;
    const { currentCaseSettings, onChangeFilter } = this.props;
    return (
      <CaseTransactionsActivityComponent
        transactions={transactions}
        onChangeFilter={onChangeFilter}
        total={totalRecord}
        rows={rows}
        openInNewPage
        key={currentCaseSettings?.name}
        loading={loading}
        selectedAlarmDomain={currentCaseSettings}
        paginationFixed={false}
        sort={sort}
        onPageChange={this.onPageChange}
        currentPage={currentPage}
        update={0}
        onSortChange={this.onSortChange}
        multiple
      />
    );
  }

  public componentDidMount = async () => {
    this.startInterval();
  };

  public componentWillUnmount = () => {
    this.stopInterval();
  };

  private startInterval = async () => {
    const { caseSettings } = this.props;
    this.intervalManager.clearTransactionIntervals();
    if (caseSettings.length == 0) return;
    this.intervalManager.addTransactionInterval(setInterval(async () => await this.onInterval(), 5_000) as any);
    await this.onInterval();
  };

  private restartInterval = async () => {
    this.stopInterval();
    await this.startInterval();
  };

  private stopInterval = () => {
    this.transactionDataCanceler?.();
  };

  private onInterval = async () => {
    const { currentCaseSettings, onFetchCount, filter, caseSettings, changeListComponentLoading } = this.props;
    const { rows, currentPage, sort, loading } = this.state;
    const transactionQuery = TransactionPageUtil.getTransactionQuery(currentCaseSettings, caseSettings, filter, rows, currentPage, sort);
    try {
      const token = axios.CancelToken.source();
      this.transactionDataCanceler = token.cancel;
      const { data: transactions, count } = await this.transactionService.getPageData(currentCaseSettings?.dataSource, transactionQuery, token.token);
      const hasTransactionChanged = transactions.length !== this.state.transactions.length || !_.isEqual(transactions, this.state.transactions);
      const hasCountChanged = count !== this.state.totalRecord;
      const hasDataChanged = hasTransactionChanged || hasCountChanged;
      if (this.reloadWhenBrowserTranslated(hasDataChanged, loading)) {
        return;
      }
      if (hasCountChanged) {
        this.setState({ totalRecord: count });
        onFetchCount(count);
      }
      if (hasTransactionChanged || transactions.length === 0 || loading) {
        this.setState({ loading: false, transactions: transactions, totalRecord: count }, () => {
          changeListComponentLoading(false);
        });
      }
    } catch (e) {}
  };

  private reloadWhenBrowserTranslated(hasDataChanged: boolean, isInitialLoad: boolean): boolean {
    if (isInitialLoad || !hasDataChanged) {
      return false;
    }
    if (!DocumentLanguage.isBrowserTranslated()) {
      return false;
    }
    window.location.reload();
    return true;
  }

  private onSortChange = (order: Order[]) => {
    const { onTablePropChange } = this.props;
    const { rows, currentPage } = this.state;
    this.setState({ sort: order, loading: true }, this.restartInterval);
    onTablePropChange(order, rows, currentPage);
  };

  private onPageChange = (page: number, rows: number) => {
    const { onTablePropChange } = this.props;
    const { sort } = this.state;
    this.setState({ currentPage: page, rows: rows, loading: true }, this.restartInterval);
    onTablePropChange(sort, rows, page);
  };
}

class State {
  loading: boolean = true;
  transactions: TransactionData[] = [];
  totalRecord: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  sort: Order[] = [];
}

interface Props extends ReduxProps {
  currentCaseSettings: CaseSettings | undefined;
  caseSettings: Pick<CaseSettings, 'id' | 'name' | 'dataSource' | 'keyField'>[];
  filter: Filter;
  onFetchCount: (value: number) => void;
  onTablePropChange: (sort: Order[], rows: number, currentPage: number) => void;
  onChangeFilter: (filter: Filter) => void;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  attributes: state.auth.attributes as UserSystemAttributes,
  case: state.case as CaseState,
});
const connector = connect(mapStateToProps, {
  clearPage,
  changePageFilter,
  changeCaseColumns,
  setAttribute,
  changeListComponentLoading,
  changeTransactionCountLoading,
});
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TransactionListComponent);
